import { useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import linkifyRegex from "remark-linkify-regex";

import { getMessageClassName } from "shared/functions";
import { isYouTubeEmbedUrl } from "shared/utils/urlValidation";

import renderers from "./renderers";
import { linkRegexp } from "./constants";
import { transformLinkUri } from "./functions";

import "./index.css";

export const remarkPlugins = [linkifyRegex(linkRegexp), remarkGfm];

type Props = {
  message: string;
  isSent: boolean;
  renderableSource?: string;
};

function MessageWithLinks(props: Props) {
  const { message, isSent, renderableSource } = props;

  const messageClassName = getMessageClassName(isSent);

  const renderSource = useCallback(() => {
    if (!renderableSource) {
      return null;
    }

    if (isYouTubeEmbedUrl(renderableSource)) {
      return (
        <div className="overflow-hidden rounded-lg pt-4">
          <iframe
            src={renderableSource}
            width={250}
            height={250}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            className="aspect-video w-full rounded-lg"
            loading="lazy"
          />
        </div>
      );
    }

    return (
      <div className="overflow-hidden rounded-lg pt-4">
        <img
          src={renderableSource}
          alt="Content"
          className="w-full rounded-lg"
        />
      </div>
    );
  }, [renderableSource]);

  return (
    <div
      className="prose prose-sm
        prose-headings:mb-1 prose-headings:mt-2 prose-headings:text-lg prose-headings:font-medium
        prose-p:my-1 prose-strong:font-medium prose-ul:my-1 prose-li:my-0
        prose-table:break-words prose-th:align-top"
    >
      <div className={messageClassName}>
        <ReactMarkdown
          remarkPlugins={remarkPlugins}
          transformLinkUri={transformLinkUri}
          components={renderers}
        >
          {message}
        </ReactMarkdown>
        {renderSource()}
      </div>
    </div>
  );
}

export default MessageWithLinks;
